/**
 * WorkBarAgentsRow — second row of the WorkBar card.
 *
 * Renders on both master threads (roster of spawned workers) and worker
 * threads (roster of sibling workers with the current thread marked as
 * "you"). Collapsed by default; clicking the header toggles expansion.
 *
 * The primary roster component. The legacy `TeamPanel.tsx` + `TeamPanelCard.tsx`
 * pair was deleted in favor of this single-source surface.
 */
import type { ThreadId } from "@cmmd-forge/contracts";
import { BotIcon, ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import { memo, useMemo, useState, type MouseEvent } from "react";

import { cn } from "~/lib/utils";
import type { ThreadTeam } from "~/storeSelectors";

import { Button } from "../ui/button";
import { buildAgentsRowViewModel } from "./WorkBarAgentsRow.logic";
import { WorkerRoleBadge } from "./WorkerRoleBadge";
import { pickWorkerActions, type WorkerCardViewModel } from "./workbarAgents.logic";

interface WorkBarAgentsRowProps {
  readonly team: ThreadTeam | undefined;
  readonly onOpenThread: (threadId: ThreadId) => void;
  readonly onWorkerAction?: (actionId: string, workerId: ThreadId) => void;
  readonly defaultExpanded?: boolean;
}

export const WorkBarAgentsRow = memo(function WorkBarAgentsRow({
  team,
  onOpenThread,
  onWorkerAction,
  defaultExpanded = false,
}: WorkBarAgentsRowProps) {
  const vm = useMemo(() => buildAgentsRowViewModel(team), [team]);
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (!vm) return null;

  const { header, team: teamVm, isWorker } = vm;
  const workerCount = teamVm.workers.length;

  return (
    <section
      data-testid="workbar-agents-row"
      role="region"
      aria-label={header.primary}
      aria-expanded={expanded}
      className="flex min-w-0 flex-col"
    >
      <button
        type="button"
        className={cn(
          "flex w-full min-w-0 items-center gap-2 px-3 py-1.5 text-left text-xs",
          "hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        )}
        onClick={() => setExpanded((v) => !v)}
        aria-controls="workbar-agents-list"
      >
        <BotIcon aria-hidden="true" className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="truncate font-medium text-foreground">{header.primary}</span>
        {header.masterLink ? (
          <>
            <span aria-hidden="true" className="shrink-0 text-muted-foreground/60">
              ·
            </span>
            <span className="flex min-w-0 shrink items-center gap-1 text-muted-foreground">
              <span className="shrink-0">Part of</span>
              <a
                href={`#thread-${header.masterLink.threadId}`}
                onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (header.masterLink) onOpenThread(header.masterLink.threadId as ThreadId);
                }}
                className="truncate text-primary hover:underline"
                data-testid="workbar-agents-master-link"
              >
                {header.masterLink.title}
              </a>
            </span>
          </>
        ) : null}
        {teamVm.attentionCount > 0 ? (
          <span
            aria-label={`${teamVm.attentionCount} worker${teamVm.attentionCount === 1 ? "" : "s"} need attention`}
            className="ml-1 inline-flex shrink-0 items-center rounded-full bg-warning-surface px-1.5 py-px text-[10px] font-medium text-warning"
          >
            ⚠ {teamVm.attentionCount}
          </span>
        ) : null}
        <span className="ml-auto flex shrink-0 items-center gap-2 text-muted-foreground">
          {teamVm.runningCount > 0 ? (
            <span className="font-mono tabular-nums text-[10px]">
              {teamVm.runningCount} running
            </span>
          ) : null}
          {workerCount > 0 ? (
            expanded ? (
              <ChevronDownIcon aria-hidden="true" className="size-3.5" />
            ) : (
              <ChevronRightIcon aria-hidden="true" className="size-3.5" />
            )
          ) : null}
        </span>
      </button>

      {expanded && workerCount > 0 ? (
        <ul
          id="workbar-agents-list"
          role="list"
          className="flex flex-col divide-y divide-border/40 border-t border-border/40"
          data-testid="workbar-agents-list"
        >
          {teamVm.workers.map((worker) => (
            <WorkerRosterRow
              key={worker.id}
              worker={worker}
              isWorkerView={isWorker}
              onOpenThread={onOpenThread}
              {...(onWorkerAction !== undefined ? { onWorkerAction } : {})}
            />
          ))}
        </ul>
      ) : null}
    </section>
  );
});

interface WorkerRosterRowProps {
  readonly worker: WorkerCardViewModel;
  readonly isWorkerView: boolean;
  readonly onOpenThread: (threadId: ThreadId) => void;
  readonly onWorkerAction?: (actionId: string, workerId: ThreadId) => void;
}

function WorkerRosterRow({
  worker,
  isWorkerView: _isWorkerView,
  onOpenThread,
  onWorkerAction,
}: WorkerRosterRowProps) {
  const actions = useMemo(() => pickWorkerActions(worker.status), [worker.status]);

  return (
    <li
      role="listitem"
      data-testid={`workbar-agents-row-${worker.id}`}
      data-current={worker.isCurrent ? "true" : undefined}
      className={cn(
        "flex min-w-0 items-center gap-3 px-3 py-1.5 text-xs",
        worker.isCurrent && "border-l-2 border-l-indigo-500/80 bg-hue-indigo-surface",
      )}
    >
      <WorkerRoleBadge role={worker.workerRole} />
      <div className="flex min-w-0 flex-1 flex-col leading-tight">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate font-medium text-foreground">{worker.title}</span>
          {worker.isCurrent ? (
            <span
              aria-label="current thread"
              className="inline-flex shrink-0 items-center rounded-sm bg-hue-indigo-surface px-1 py-0 text-[9px] font-semibold uppercase tracking-wide text-hue-indigo-foreground"
            >
              you
            </span>
          ) : null}
          <span
            className={cn(
              "shrink-0 rounded-sm px-1 py-0 text-[10px] uppercase tracking-wide",
              statusToneClass(worker.status),
            )}
          >
            {formatStatus(worker.status)}
          </span>
        </div>
        {worker.branch ? (
          <span className="truncate font-mono text-[10px] text-muted-foreground">
            {worker.branch}
          </span>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {!worker.isCurrent ? (
          <Button size="xs" variant="outline" onClick={() => onOpenThread(worker.id as ThreadId)}>
            Open
          </Button>
        ) : null}
        {actions.map((a) => (
          <Button
            key={a.id}
            size="xs"
            variant={
              a.intent === "primary"
                ? "default"
                : a.intent === "destructive"
                  ? "destructive"
                  : "ghost"
            }
            onClick={() => onWorkerAction?.(a.id, worker.id as ThreadId)}
          >
            {a.label}
          </Button>
        ))}
      </div>
    </li>
  );
}

function formatStatus(status: WorkerCardViewModel["status"]): string {
  switch (status) {
    case "needs-review":
      return "review";
    default:
      return status;
  }
}

function statusToneClass(status: WorkerCardViewModel["status"]): string {
  switch (status) {
    case "running":
      return "bg-info-surface text-info-foreground";
    case "stuck":
    case "conflicted":
      return "bg-warning-surface text-warning";
    case "needs-review":
    case "ready":
      return "bg-success-surface text-success-foreground";
    case "merged":
      return "bg-hue-violet-surface text-hue-violet-foreground";
    case "archived":
      return "bg-muted text-muted-foreground";
    case "idle":
    default:
      return "bg-muted text-muted-foreground";
  }
}
